import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import '@/App.css';

const FutureLogo = ({ size = 48 }) => (
  <img src="/images/future-logo-symbol.png" alt="Future" width={size} height={size} className="object-contain" style={{ filter: 'brightness(1.1)' }} />
);

const LoadingScreen = () => (
  <motion.div
    className="fixed inset-0 z-[9999] bg-[#091a16] flex flex-col items-center justify-center"
    exit={{ opacity: 0 }}
    transition={{ duration: 0.6 }}
    data-testid="loading-screen"
  >
    <div className="load-pulse"><FutureLogo size={48} /></div>
    <motion.span
      className="mt-6 font-anton text-cream text-xl tracking-[0.12em]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}
    >FUTURE</motion.span>
  </motion.div>
);

const Gateway = lazy(() => import('@/pages/Gateway'));
const InvestorsLanding = lazy(() => import('@/pages/investors/InvestorsLanding'));
const FoundersLanding = lazy(() => import('@/pages/founders/FoundersLanding'));
const BuildersLanding = lazy(() => import('@/pages/builders/BuildersLanding'));
const Portfolio = lazy(() => import('@/pages/shared/Portfolio'));
const TrackRecord = lazy(() => import('@/pages/investors/TrackRecord'));
const DealRoom = lazy(() => import('@/pages/investors/DealRoom'));
const Stories = lazy(() => import('@/pages/founders/Stories'));
const Accelerate = lazy(() => import('@/pages/founders/Accelerate'));
const Ventures = lazy(() => import('@/pages/builders/Ventures'));
const Programs = lazy(() => import('@/pages/builders/Programs'));
const Fellowship = lazy(() => import('@/pages/builders/Fellowship'));
const About = lazy(() => import('@/pages/shared/About'));
const CommunityMarketing = lazy(() => import('@/pages/community/CommunityMarketing'));
const Login = lazy(() => import('@/pages/community/Login'));
const Dashboard = lazy(() => import('@/pages/community/Dashboard'));

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
      >
        <Routes location={location}>
          <Route path="/" element={<Gateway />} />
          <Route path="/investors" element={<InvestorsLanding />} />
          <Route path="/investors/portfolio" element={<Portfolio site="investors" />} />
          <Route path="/investors/track-record" element={<TrackRecord />} />
          <Route path="/investors/invest" element={<DealRoom />} />
          <Route path="/investors/about" element={<About site="investors" />} />
          <Route path="/founders" element={<FoundersLanding />} />
          <Route path="/founders/stories" element={<Stories />} />
          <Route path="/founders/portfolio" element={<Portfolio site="founders" />} />
          <Route path="/founders/accelerate" element={<Accelerate />} />
          <Route path="/founders/about" element={<About site="founders" />} />
          <Route path="/builders" element={<BuildersLanding />} />
          <Route path="/builders/ventures" element={<Ventures />} />
          <Route path="/builders/programs" element={<Programs />} />
          <Route path="/builders/fellowship" element={<Fellowship />} />
          <Route path="/builders/portfolio" element={<Portfolio site="builders" />} />
          <Route path="/builders/about" element={<About site="builders" />} />
          <Route path="/community" element={<CommunityMarketing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/community-dashboard" element={<Dashboard />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1800);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="App">
      <AnimatePresence mode="wait">
        {loading && <LoadingScreen key="loader" />}
      </AnimatePresence>
      {!loading && (
        <BrowserRouter>
          <div className="film-grain" />
          <div className="accent-line" />
          <Suspense fallback={<LoadingScreen />}>
            <AnimatedRoutes />
          </Suspense>
        </BrowserRouter>
      )}
    </div>
  );
}

export default App;
